import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { NextIcon } from '@platform/ui-kit';

/**
 * The title-and-toolbar block on a ported screen.
 *
 * Deliberately NOT the platform's `PageHeader`, which puts the actions beside the
 * title: with six buttons the title is squeezed into a narrow column and wraps down
 * the page behind them.
 *
 * Here the heading sits on its own line and the toolbar sits BENEATH it, on a row
 * that wraps. Two things that are easy to get wrong and were:
 *
 *  - the toolbar must not be `overflow-x: auto` with `justify-content: flex-end`.
 *    Once the buttons are wider than the row, that combination scrolls the FIRST
 *    button out of view — it looks like Add Application has vanished.
 *  - buttons must not shrink or wrap their own labels, or "Add Application" breaks
 *    across two lines. They wrap as whole buttons onto the next row instead.
 */
export interface ScreenHeaderProps {
  title: string;
  actions: ReactNode;
  /** Rendered opposite the trail — the help button. */
  aside?: ReactNode;
  /**
   * APR's in-page trail, e.g. Portfolio › Application Master Data.
   *
   * Deliberately a SECOND trail: the shell's breadcrumb in the app bar names the
   * navigation section ("Applications"), while this names the screen
   * ("Application Master Data"), which is what the source app shows and what people
   * cite. The last crumb is the current page and is not a link.
   */
  trail?: readonly { readonly label: string; readonly path?: string }[];
  onNavigate?: (path: string) => void;
  /**
   * Where the toolbar sits. APR puts it on the title's row; `below` is used where the
   * owner asked for it, and keeps the title from being squeezed by a wide toolbar.
   */
  actionsPosition?: 'inline' | 'below';
}

export function ScreenHeader({
  title,
  actions,
  aside,
  trail,
  onNavigate,
  actionsPosition = 'below',
}: ScreenHeaderProps) {
  const inline = actionsPosition === 'inline';
  return (
    <Box sx={{ mb: 2 }}>
      {trail?.length || aside ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {trail?.map((crumb, index) => (
            <Stack key={crumb.label} direction="row" alignItems="center" spacing={1}>
              {index > 0 ? <NextIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> : null}
              {crumb.path && onNavigate ? (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => onNavigate(crumb.path!)}
                  sx={{ fontSize: 13, color: 'text.secondary' }}
                >
                  {crumb.label}
                </Link>
              ) : (
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{crumb.label}</Typography>
              )}
            </Stack>
          ))}
          <Box sx={{ flex: 1 }} />
          {aside}
        </Stack>
      ) : null}

      <Box
        sx={{
          display: inline ? 'flex' : 'block',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: 22,
            fontWeight: 600,
            color: '#212121',
            lineHeight: '26.4px',
            mb: inline ? 0 : 1.5,
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            ...(inline ? { flex: 1, justifyContent: 'flex-end' } : {}),
            '& .MuiButton-root': { whiteSpace: 'nowrap', flexShrink: 0 },
          }}
        >
          {actions}
        </Box>
      </Box>
    </Box>
  );
}
